import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import configService from '../services/ConfigService';
import { GOOGLE_PLAY_URL, APP_STORE_URL } from './MobileAppBanner';
import { debugLog, debugError } from '../utils/debugUtils';

function compareVersions(current, latest) {
  const parse = (v) => v.split('.').map(Number);
  const [ca, cb, cc] = parse(current);
  const [la, lb, lc] = parse(latest);
  if (la > ca) return true;
  if (la === ca && lb > cb) return true;
  if (la === ca && lb === cb && lc > cc) return true;
  return false;
}

export default function VersionCheckManager() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [latestVersion, setLatestVersion] = useState('');
  const [forceUpdate, setForceUpdate] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const platform = Capacitor.getPlatform(); // 'ios' | 'android'

    const check = async () => {
      try {
        const config = await configService.load();
        const minVersion = config[`MIN_VERSION_${platform.toUpperCase()}`];
        if (!minVersion) return;
        const hasUpdate = compareVersions(process.env.REACT_APP_VERSION, minVersion);
        if (hasUpdate) {
          debugLog('[VersionCheckManager] Update available:', minVersion);
          setLatestVersion(minVersion);
          setForceUpdate(config[`FORCE_UPDATE_${platform.toUpperCase()}`] === 'true');
          setOpen(true);
        }
      } catch (err) {
        debugError('[VersionCheckManager] Version check failed:', err);
      }
    };

    check();
  }, []);

  const handleLater = () => {
    if (!forceUpdate) setOpen(false);
  };

  const handleUpdateNow = () => {
    const storeUrl = Capacitor.getPlatform() === 'ios' ? APP_STORE_URL : GOOGLE_PLAY_URL;
    Browser.open({ url: storeUrl });
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleLater} disableEscapeKeyDown={forceUpdate}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SystemUpdateIcon color="primary" />
        {t('version.updateAvailable')}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {t('version.updateMessage', { version: latestVersion })}
        </Typography>
      </DialogContent>
      <DialogActions>
        {!forceUpdate && (
          <Button onClick={handleLater} color="inherit">
            {t('version.later')}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleUpdateNow}
        >
          {t('version.updateNow')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
